// Image editor compositor.
//
// Bakes every non-destructive edit (crop, rotate, flip, finetune, filter,
// redact, sticker, annotate, text overlays, shape mask, resize) into a single
// flattened bitmap and returns it as a Blob. Consumed by ImageEditorDialog.
//
// Coordinate convention for overlays: all positions/sizes are NORMALISED to the
// cropped output box (0..1). x/y are fractions of width/height; font/stroke
// sizes are fractions of the output height / diagonal. This keeps overlays
// stable if the output is later resized and independent of display zoom.

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // blob: URLs must not carry crossOrigin; http(s) should for canvas export.
    if (!/^blob:/i.test(url)) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = (err) => reject(err);
    image.src = url;
  });
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const getRadianAngle = (deg) => (deg * Math.PI) / 180;

// Make sure every web font used by a text overlay is loaded before we draw it
// to the canvas — otherwise the export silently falls back to a system font.
async function ensureTextFonts(overlays = []) {
  if (typeof document === 'undefined' || !document.fonts) return;
  const texts = overlays.filter((o) => o.type === 'text' && o.text);
  await Promise.all(texts.map((o) => {
    const first = String(o.fontFamily || 'sans-serif').split(',')[0].trim();
    const weight = o.weight || 700;
    return document.fonts.load(`${weight} 48px ${first}`).catch(() => {});
  }));
}

// Bounding box of an image after it is rotated by `rotation` degrees.
function rotateSize(width, height, rotation) {
  const rad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
}

export function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// Finetune / filter → CSS filter string (shared by live preview + export)
// ---------------------------------------------------------------------------

export const DEFAULT_FINETUNE = {
  brightness: 100, // %
  contrast: 100, // %
  saturation: 100, // %
  warmth: 0, // -100..100 (sepia + hue nudge)
  blur: 0, // px
};

// Build a canvas/CSS `filter` string from finetune values + an optional preset
// filter fragment (from the Filter tool). `scale` lets export match a preview
// rendered at a different pixel size for blur (px are resolution-dependent).
export function buildFilterString(finetune = {}, presetCss = '', scale = 1) {
  const f = { ...DEFAULT_FINETUNE, ...finetune };
  const parts = [];
  if (f.brightness !== 100) parts.push(`brightness(${f.brightness}%)`);
  if (f.contrast !== 100) parts.push(`contrast(${f.contrast}%)`);
  if (f.saturation !== 100) parts.push(`saturate(${f.saturation}%)`);
  if (f.warmth) {
    // Positive warmth = warmer (sepia); negative = cooler (hue toward blue).
    if (f.warmth > 0) parts.push(`sepia(${Math.min(100, f.warmth)}%)`);
    else parts.push(`hue-rotate(${Math.round(f.warmth * 0.9)}deg) saturate(115%)`);
  }
  if (f.blur) parts.push(`blur(${(f.blur * scale).toFixed(2)}px)`);
  if (presetCss) parts.push(presetCss);
  return parts.join(' ').trim() || 'none';
}

// Filter presets: each maps to a finetune override + optional extra css.
export const FILTER_PRESETS = [
  { key: 'none', label: 'Original', finetune: {}, css: '' },
  { key: 'vivid', label: 'Vivid', finetune: { saturation: 150, contrast: 110 }, css: '' },
  { key: 'warm', label: 'Warm', finetune: { warmth: 45, brightness: 104 }, css: '' },
  { key: 'cool', label: 'Cool', finetune: { warmth: -45, brightness: 102 }, css: '' },
  { key: 'mono', label: 'Mono', finetune: { saturation: 0, contrast: 108 }, css: '' },
  { key: 'noir', label: 'Noir', finetune: { saturation: 0, contrast: 135, brightness: 92 }, css: '' },
  { key: 'fade', label: 'Fade', finetune: { contrast: 85, brightness: 106, saturation: 85 }, css: '' },
  { key: 'chrome', label: 'Chrome', finetune: { contrast: 115, saturation: 120 }, css: 'brightness(103%)' },
];

// ---------------------------------------------------------------------------
// Stage 1 — crop + rotate + flip → a base bitmap canvas
// ---------------------------------------------------------------------------

// Returns a canvas containing exactly the cropped region, with rotation and
// flips already baked in. No colour filters applied here.
function getBaseCanvas(image, croppedAreaPixels, rotation = 0, flipH = false, flipV = false) {
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxW, height: bBoxH } = rotateSize(image.width, image.height, rotation);

  // Draw the fully-transformed image onto a bounding-box canvas.
  const full = document.createElement('canvas');
  full.width = Math.round(bBoxW);
  full.height = Math.round(bBoxH);
  const fctx = full.getContext('2d');
  fctx.translate(bBoxW / 2, bBoxH / 2);
  fctx.rotate(rotRad);
  fctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  fctx.translate(-image.width / 2, -image.height / 2);
  fctx.drawImage(image, 0, 0);

  const crop = croppedAreaPixels || { x: 0, y: 0, width: full.width, height: full.height };
  const cw = Math.max(1, Math.round(crop.width));
  const ch = Math.max(1, Math.round(crop.height));

  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const octx = out.getContext('2d');
  // Copy just the crop rectangle out of the transformed frame.
  octx.drawImage(full, Math.round(crop.x), Math.round(crop.y), cw, ch, 0, 0, cw, ch);
  return out;
}

// ---------------------------------------------------------------------------
// Overlay drawing (text / sticker / annotate / redact) in output px space
// ---------------------------------------------------------------------------

function drawRedact(ctx, base, o, W, H) {
  const x = o.x * W;
  const y = o.y * H;
  const w = o.w * W;
  const h = o.h * H;
  if (w < 1 || h < 1) return;
  ctx.save();
  roundRectPath(ctx, x, y, w, h, Math.min(w, h) * 0.04);
  ctx.clip();
  if (o.mode === 'block') {
    ctx.fillStyle = o.color || '#000';
    ctx.fillRect(x, y, w, h);
  } else if (o.mode === 'pixelate') {
    const px = Math.max(2, Math.round(Math.min(w, h) / 12));
    const tmp = document.createElement('canvas');
    tmp.width = Math.max(1, Math.round(w / px));
    tmp.height = Math.max(1, Math.round(h / px));
    const tctx = tmp.getContext('2d');
    tctx.imageSmoothingEnabled = false;
    tctx.drawImage(base, x, y, w, h, 0, 0, tmp.width, tmp.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tmp, 0, 0, tmp.width, tmp.height, x, y, w, h);
    ctx.imageSmoothingEnabled = true;
  } else {
    // blur (default)
    ctx.filter = `blur(${Math.max(3, Math.round(Math.min(w, h) / 8))}px)`;
    ctx.drawImage(base, x, y, w, h, x, y, w, h);
    ctx.filter = 'none';
  }
  ctx.restore();
}

function drawStrokeOverlay(ctx, o, W, H) {
  const pts = (o.points || []).map((p) => ({ x: p.x * W, y: p.y * H }));
  if (!pts.length) return;
  const diag = Math.hypot(W, H);
  ctx.save();
  ctx.strokeStyle = o.color || '#FF3B30';
  ctx.fillStyle = o.color || '#FF3B30';
  ctx.lineWidth = Math.max(1, (o.stroke || 0.01) * diag);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (o.shape === 'rect') {
    const [a, b] = [pts[0], pts[pts.length - 1]];
    ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  } else if (o.shape === 'ellipse') {
    const [a, b] = [pts[0], pts[pts.length - 1]];
    ctx.beginPath();
    ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (o.shape === 'line' || o.shape === 'arrow') {
    const [a, b] = [pts[0], pts[pts.length - 1]];
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    if (o.shape === 'arrow') {
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      const head = ctx.lineWidth * 3.2;
      ctx.beginPath();
      ctx.moveTo(b.x, b.y);
      ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7), b.y - head * Math.sin(ang - Math.PI / 7));
      ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7), b.y - head * Math.sin(ang + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    }
  } else {
    // pen — freehand polyline
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    pts.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.stroke();
  }
  ctx.restore();
}

function drawTextOverlay(ctx, o, W, H) {
  const cx = o.x * W;
  const cy = o.y * H;
  const fontPx = Math.max(6, (o.fontSize || 0.08) * H);
  const weight = o.weight || 700;
  const family = o.fontFamily || 'Inter, sans-serif';
  ctx.save();
  ctx.translate(cx, cy);
  if (o.rotation) ctx.rotate(getRadianAngle(o.rotation));
  ctx.font = `${weight} ${fontPx}px ${family}`;
  ctx.textBaseline = 'top';
  ctx.textAlign = o.align || 'center';
  const lines = String(o.text || '').split('\n');
  const lineH = fontPx * 1.18;
  const widths = lines.map((l) => ctx.measureText(l).width);
  const boxW = Math.max(1, ...widths);
  const boxH = lineH * lines.length;
  const pad = fontPx * 0.28;
  // Background pill
  if (o.bg) {
    ctx.fillStyle = o.bg;
    roundRectPath(ctx, -boxW / 2 - pad, -boxH / 2 - pad * 0.6, boxW + pad * 2, boxH + pad * 1.2, fontPx * 0.22);
    ctx.fill();
  }
  ctx.fillStyle = o.color || '#FFFFFF';
  lines.forEach((line, i) => {
    const y = -boxH / 2 + i * lineH;
    let x = 0;
    if (o.align === 'left') x = -boxW / 2;
    else if (o.align === 'right') x = boxW / 2;
    ctx.fillText(line, x, y);
  });
  ctx.restore();
}

async function drawSticker(ctx, o, W, H) {
  const cx = o.x * W;
  const cy = o.y * H;
  const size = (o.size || 0.16) * Math.min(W, H);
  ctx.save();
  ctx.translate(cx, cy);
  if (o.rotation) ctx.rotate(getRadianAngle(o.rotation));
  if (o.src) {
    try {
      const img = await createImage(o.src);
      const ar = img.width / img.height || 1;
      const w = size;
      const h = size / ar;
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
    } catch {
      /* ignore broken sticker image */
    }
  } else if (o.char) {
    ctx.font = `${size}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(o.char, 0, 0);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

/**
 * Compose all edits into a single Blob.
 * @param {string} src image URL (blob: or http)
 * @param {object} editState
 *   crop:            {x,y,width,height}  croppedAreaPixels from react-easy-crop
 *   rotation:        number (deg)
 *   flipH, flipV:    boolean
 *   finetune:        {brightness,contrast,saturation,warmth,blur}
 *   presetCss:       string (extra filter fragment)
 *   overlays:        Array<redact|stroke|text|sticker overlay>
 *   shape:           'rect'|'square'|'circle'|'rounded'
 *   resize:          {width,height} | null
 *   mime:            'image/jpeg'|'image/png'
 * @returns {Promise<Blob>}
 */
export async function composeEditedImage(src, editState = {}) {
  const {
    crop,
    rotation = 0,
    flipH = false,
    flipV = false,
    finetune = {},
    presetCss = '',
    overlays = [],
    shape = 'rect',
    resize = null,
    mime = 'image/jpeg',
  } = editState;

  const image = await createImage(src);
  const base = getBaseCanvas(image, crop, rotation, flipH, flipV);

  // Output size (resize scales everything; overlays are normalised so they follow).
  let W = base.width;
  let H = base.height;
  if (resize && resize.width && resize.height) {
    W = Math.max(1, Math.round(resize.width));
    H = Math.max(1, Math.round(resize.height));
  }

  const flat = document.createElement('canvas');
  flat.width = W;
  flat.height = H;
  const ctx = flat.getContext('2d');

  // 1) base image with colour finetune/filter
  ctx.filter = buildFilterString(finetune, presetCss);
  ctx.drawImage(base, 0, 0, base.width, base.height, 0, 0, W, H);
  ctx.filter = 'none';

  // 2) overlays in paint order: redact → strokes → stickers → text
  await ensureTextFonts(overlays);
  const ordered = [
    ...overlays.filter((o) => o.type === 'redact'),
    ...overlays.filter((o) => o.type === 'stroke'),
    ...overlays.filter((o) => o.type === 'sticker'),
    ...overlays.filter((o) => o.type === 'text'),
  ];
  for (const o of ordered) {
    if (o.type === 'redact') drawRedact(ctx, base, o, W, H);
    else if (o.type === 'stroke') drawStrokeOverlay(ctx, o, W, H);
    else if (o.type === 'text') drawTextOverlay(ctx, o, W, H);
    else if (o.type === 'sticker') await drawSticker(ctx, o, W, H);  
  }

  // 3) shape mask (logo mode) — circle / rounded need transparency → PNG
  const needsMask = shape === 'circle' || shape === 'rounded';
  const outMime = needsMask || mime === 'image/png' ? 'image/png' : 'image/jpeg';
  let finalCanvas = flat;
  if (needsMask) {
    const masked = document.createElement('canvas');
    masked.width = W;
    masked.height = H;
    const mctx = masked.getContext('2d');
    if (shape === 'circle') {
      mctx.beginPath();
      mctx.arc(W / 2, H / 2, Math.min(W, H) / 2, 0, Math.PI * 2);
      mctx.closePath();
      mctx.clip();
    } else {
      roundRectPath(mctx, 0, 0, W, H, Math.min(W, H) * 0.18);
      mctx.clip();
    }
    mctx.drawImage(flat, 0, 0);
    finalCanvas = masked;
  }

  return new Promise((resolve, reject) => {
    finalCanvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))),
      outMime,
      0.92
    );
  });
}

// ---------------------------------------------------------------------------
// Back-compat: simple crop used before the full editor existed.
// ---------------------------------------------------------------------------

export async function getCroppedImg(src, croppedAreaPixels, mimeType = 'image/jpeg') {
  return composeEditedImage(src, { crop: croppedAreaPixels, mime: mimeType });
}

export default composeEditedImage;
